import React, { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { LuArrowRight, LuCalendarClock, LuCalendarDays, LuCalendarPlus, LuLayoutList, LuRefreshCw, LuSettings, LuZap } from 'react-icons/lu';
import { API_BASE_URL } from '../config.js';
import { WeeklyReportList, WeekTile } from '../components/weekly/WeeklyReportList';
import ReportStatusBadges from '../components/weekly/ReportStatusBadges';
import CreateReportModal from '../components/weekly/CreateReportModal';
import PeriodBox from '../components/weekly/PeriodBox';
import { isPublished } from '../components/weekly/reportStatus.js';
import { withJail } from '../components/weekly/jail.js';
import { generateNowPeriod, periodDays, slotOf } from '../utils/reportPeriod.js';
import {
  formatDays, formatParisInstant, formatPeriodRange, formatReportRange, formatSlot, overlapNote, overlapsByReport,
} from '../utils/weekFormat.js';

import { sourceLabel } from '../utils/sources.js';
import PageHeader from '../components/ui/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import SegmentedControl from '../components/ui/SegmentedControl';
import EmptyState from '../components/ui/EmptyState';
import Skeleton from '../components/ui/Skeleton';
import ConfirmModal from '../components/ui/ConfirmModal';

const POLL_DELAY_MS = 5000;

const FILTERS = {
  all: () => true,
  published: (w) => isPublished(w),
  draft: (w) => !isPublished(w),
};

/** Highlighted entry point: the most recent report the viewer can open. */
const LatestReportCard = ({ report, clientSlug, isAdminUser, jail }) => (
  <section className="rounded-2xl border border-[#e6e3fb] bg-gradient-to-br from-[#f6f5ff] to-[#fbfbfe] p-4 dark:border-gray-900 dark:from-[#1c1830] dark:to-[#161421] sm:p-5">
    <div className="flex flex-wrap items-center gap-4">
      <WeekTile week={report.week_number} solid large />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-brand-600 dark:text-brand-400">Dernier rapport</p>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Semaine {report.week_number} · {report.year}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">{formatReportRange(report, { long: true })}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ReportStatusBadges report={report} isAdminUser={isAdminUser} size="sm" />
        </div>
      </div>
      <Button as={Link} to={withJail(`/w/${clientSlug}/${report.id}`, jail)} variant="primary" iconRight={LuArrowRight} className="w-full sm:w-auto">
        Ouvrir le rapport
      </Button>
    </div>
  </section>
);

const WeeklyReportHome = ({ fetchWithAuth, currentUser }) => {
  const { slug } = useParams();
  const { client, weeks, loading, isAdminUser, upsertWeek, reloadWeeks, jail } = useOutletContext();
  const [filter, setFilter] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // Generate-now / regenerate dialog: { kind: 'generate' | 'regen', report?, pending, error }.
  const [run, setRun] = useState(null);
  const overlaps = useMemo(() => overlapsByReport(weeks), [weeks]);
  const [pollTick, setPollTick] = useState(0);

  // A running dump finishes server side: refresh the list until none is running.
  const dumping = weeks.some((w) => w.steps?.dump === 'started');
  useEffect(() => {
    if (!dumping) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      await reloadWeeks();
      if (!cancelled) setPollTick((n) => n + 1);
    }, POLL_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [dumping, pollTick, reloadWeeks]);

  const counts = useMemo(() => ({
    all: weeks.length,
    published: weeks.filter(FILTERS.published).length,
    draft: weeks.filter(FILTERS.draft).length,
  }), [weeks]);
  const visibleWeeks = useMemo(() => weeks.filter(FILTERS[filter] || FILTERS.all), [weeks, filter]);

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-12 w-72" />
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-80 w-full rounded-xl" />
      </div>
    );
  }

  const latest = weeks[0];
  const slot = slotOf(client);

  const startRun = async () => {
    if (!run || run.pending) return;
    setRun({ ...run, pending: true, error: null });
    const url = run.kind === 'regen'
      ? `${API_BASE_URL}/api/weekly/client/${slug}/week/${run.report.id}/regenerate`
      : `${API_BASE_URL}/api/weekly/client/${slug}/generate-now`;
    try {
      const response = await fetchWithAuth(url, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'La génération n\'a pas pu être lancée.');
      setRun(null);
      reloadWeeks();
    } catch (err) {
      setRun((current) => current && { ...current, pending: false, error: err.message || 'La génération n\'a pas pu être lancée.' });
    }
  };

  const nextPeriod = run?.kind === 'generate'
    ? generateNowPeriod({ latest, ...slot, scheduleUpdatedAt: client?.schedule_updated_at })
    : null;
  const nextNote = nextPeriod && overlapNote(nextPeriod.ongoing ? weeks[1] : latest, nextPeriod);
  const regenReport = run?.kind === 'regen' ? run.report : null;
  const regenOverlap = regenReport ? overlaps.get(regenReport.id) : null;

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={client?.name}
        title="Rapports hebdomadaires"
        subtitle={(
          <>
            {`${weeks.length} rapport${weeks.length > 1 ? 's' : ''} · Source ${sourceLabel(client?.source_type)}`}
            <span className="mt-1 flex items-center gap-1.5">
              <LuCalendarClock className="h-4 w-4 flex-shrink-0" aria-hidden />
              Généré chaque {formatSlot(slot.day, slot.hour)}
            </span>
          </>
        )}
        actions={(
          <>
            {currentUser?.issuperadmin && client && (
              <Button as={Link} to={`/admin/configs?configId=${client.id}`} icon={LuSettings}>Configuration</Button>
            )}
            {isAdminUser && (
              <Button icon={LuZap} onClick={() => setRun({ kind: 'generate', pending: false, error: null })}>Générer maintenant</Button>
            )}
            {isAdminUser && (
              <Button variant="primary" icon={LuCalendarPlus} onClick={() => setIsCreateOpen(true)}>Nouveau rapport</Button>
            )}
          </>
        )}
      />

      {weeks.length === 0 ? (
        <EmptyState
          bordered
          icon={LuCalendarDays}
          title="Aucun rapport disponible"
          description={isAdminUser
            ? 'Créez un rapport pour une semaine passée : les messages et les photos seront récupérés, puis analysés.'
            : 'Les rapports de ce client apparaîtront ici dès leur publication.'}
          action={isAdminUser && (
            <Button variant="primary" icon={LuCalendarPlus} onClick={() => setIsCreateOpen(true)}>Créer le premier rapport</Button>
          )}
        />
      ) : (
        <>
          {latest && <LatestReportCard report={latest} clientSlug={slug} isAdminUser={isAdminUser} jail={jail} />}

          <Card>
            <CardHeader
              icon={LuLayoutList}
              title="Tous les rapports"
              subtitle="Du plus récent au plus ancien"
              actions={isAdminUser && (
                <SegmentedControl
                  size="sm"
                  ariaLabel="Filtrer les rapports"
                  value={filter}
                  onChange={setFilter}
                  options={[
                    { value: 'all', label: 'Tous', count: counts.all },
                    { value: 'published', label: 'Publiés', count: counts.published },
                    { value: 'draft', label: 'Brouillons', count: counts.draft },
                  ]}
                />
              )}
            />
            {visibleWeeks.length === 0 ? (
              <EmptyState
                icon={LuCalendarDays}
                tone="gray"
                title={filter === 'draft' ? 'Aucun brouillon' : 'Aucun rapport publié'}
                description="Changez de filtre pour voir les autres rapports."
              />
            ) : (
              <WeeklyReportList
                weeks={visibleWeeks}
                clientSlug={slug}
                isAdminUser={isAdminUser}
                jail={jail}
                overlaps={overlaps}
                onRegenerate={isAdminUser ? (report) => setRun({ kind: 'regen', report, pending: false, error: null }) : null}
              />
            )}
          </Card>
        </>
      )}

      {isAdminUser && (
        <CreateReportModal
          jail={jail}
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onCreated={upsertWeek}
          clientSlug={slug}
          fetchWithAuth={fetchWithAuth}
          existingReports={weeks}
          reportDay={slot.day}
          reportHour={slot.hour}
        />
      )}

      {isAdminUser && (
        <ConfirmModal
          isOpen={!!run}
          onClose={() => setRun(null)}
          onConfirm={startRun}
          loading={!!run?.pending}
          tone="brand"
          icon={run?.kind === 'regen' ? LuRefreshCw : LuZap}
          title={run?.kind === 'regen' ? `Régénérer le rapport S${run.report.week_number} ?` : 'Générer un instantané maintenant ?'}
          confirmLabel={run?.kind === 'regen' ? 'Régénérer' : 'Générer maintenant'}
          message={(
            <>
              {run?.error && <Alert tone="danger" className="mb-3">{run.error}</Alert>}
              {run?.kind === 'regen'
                ? 'Les messages et photos de la période sont récupérés à nouveau, puis les analyses recalculées. Les modifications manuelles de ce rapport seront perdues. Son statut est conservé.'
                : 'Un rapport de la période en cours est créé tout de suite. Il sera complété automatiquement à l\'heure prévue, puis se comportera comme un rapport normal.'}
              {nextPeriod && (
                <PeriodBox label="Période en cours">
                  {formatParisInstant(nextPeriod.start)} <span className="text-gray-400" aria-hidden>→</span> aujourd'hui
                  <span className="text-gray-500 dark:text-gray-400"> · complété {formatParisInstant(nextPeriod.end, { separator: ' à ' })}</span>
                </PeriodBox>
              )}
              {nextNote && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{nextNote}</p>}
              {regenReport && (
                <PeriodBox label="Période du rapport (inchangée)">
                  {regenReport.period_start ? formatPeriodRange(regenReport) : formatReportRange(regenReport)}
                  {regenReport.period_start && (
                    <span className="text-gray-500 dark:text-gray-400">
                      {` · ${formatDays(periodDays(regenReport.period_start, regenReport.period_end))} jours`}
                      {regenOverlap && ` · reprend ${formatDays(regenOverlap.days)} j de S${regenOverlap.week}`}
                    </span>
                  )}
                </PeriodBox>
              )}
            </>
          )}
        />
      )}
    </div>
  );
};

export default WeeklyReportHome;
